import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState, useMemo, useRef, useEffect } from 'react';
import useStyles from './ContentFeature.styles';
import { useBackgroundClass, useTextColor } from '../../styles/jss/utils';
import SplitContentBySlash from '../../utils/SplitContentBySlash';
import { getMinHeightClass, SiteContext } from '../../utils/common';
import ImageContainer from './ImageContainer/ImageContainer';
import ContentContainer, { getTextAlignmentClass } from './ContentContainer/ContentContainer';
import VideoContainer from './VideoContainer/VideoContainer';
import ShoppableImage from '../ShoppableContent/ShoppableImage/ShoppableImage';
import clsx from 'clsx';
import { Box, Grid2, useMediaQuery } from '@mui/material';
import ShoppableVideo from '../ShoppableContent/ShoppableVideo/ShoppableVideo';
import Link from '../Link/Link';
const ContentFeature = (props) => {
    var _a, _b, _c;
    const { isFlyingFlowers } = useContext(SiteContext);
    const { headingTag = 'H3', textContentPosition = 'CENTER', titleTextVariant, contentPosition, shoppableImage, shoppableVideo, height = 'LARGE', links, buttonVariant, contentFeatureAnalytics, linkStyle, textAlignment, backgroundColour, backgroundAndTextColours, newStyle, } = props;
    const hasVideo = !!((_a = props === null || props === void 0 ? void 0 : props.video) === null || _a === void 0 ? void 0 : _a.url);
    const [isShowMore, setIsShowMore] = useState(false);
    // Ref to capture initial container height on mobile for fixed height overlay
    const containerRef = useRef(null);
    const [initialHeight, setInitialHeight] = useState();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    // Helper function to determine if we should use new mobile layout
    const shouldUseNewMobileLayout = newStyle && isMobile && !hasVideo;
    // Capture initial height when component mounts on mobile
    useEffect(() => {
        if (shouldUseNewMobileLayout && containerRef.current && !initialHeight) {
            const height = containerRef.current.offsetHeight;
            setInitialHeight(height);
        }
    }, [shouldUseNewMobileLayout, initialHeight]);
    const { textColour, backgroundColour: newBrandBgColour } = (_b = backgroundAndTextColours === null || backgroundAndTextColours === void 0 ? void 0 : backgroundAndTextColours[0]) !== null && _b !== void 0 ? _b : {
        textColour: undefined,
        backgroundColour: undefined,
    };
    const { classes } = useStyles({ isQuotation: !!props.isQuotation, textColour, newStyle, isShowMore });
    const titleHeadingColor = textColour && titleTextVariant === 'LARGE_ADVERTISING' && !isFlyingFlowers ? classes.headingColor : undefined;
    const getHeadingContent = () => {
        if (isFlyingFlowers) {
            return props.heading;
        }
        return (_jsx(SplitContentBySlash, { content: props.heading, classNames: { headingColor: titleHeadingColor, headingLine: classes.headingLine } }));
    };
    const renderHeading = getHeadingContent();
    const getBgColor = () => {
        var _a, _b, _c;
        if (isFlyingFlowers && ((_a = backgroundColour === null || backgroundColour === void 0 ? void 0 : backgroundColour[0]) === null || _a === void 0 ? void 0 : _a.backgroundColour)) {
            return backgroundColour[0].backgroundColour;
        }
        if (titleTextVariant && newBrandBgColour) {
            return newBrandBgColour;
        }
        return ((_c = (_b = backgroundColour === null || backgroundColour === void 0 ? void 0 : backgroundColour[0]) === null || _b === void 0 ? void 0 : _b.backgroundColour) !== null && _c !== void 0 ? _c : '#FFFFFF');
    };
    const bgColor = getBgColor();
    const isHexColor = typeof bgColor === 'string' && bgColor.startsWith('#');
    // Always call hooks unconditionally
    const backgroundClassResult = useBackgroundClass(bgColor);
    const textColorResult = useTextColor(bgColor);
    // Background styling logic optimized
    const backgroundStyling = useMemo(() => {
        if (isHexColor) {
            const { textColor, backgroundColor } = textColorResult;
            return { bgClass: '', hexTextColor: textColor, hexBackgroundColor: backgroundColor };
        }
        return { bgClass: backgroundClassResult.bgClass, hexTextColor: '', hexBackgroundColor: '' };
    }, [isHexColor, textColorResult, backgroundClassResult]);
    const { bgClass, hexTextColor, hexBackgroundColor } = backgroundStyling;
    const positionClassMap = {
        LEFT: classes.positionLeft,
        RIGHT: classes.positionRight,
    };
    const positionClass = (_c = positionClassMap[contentPosition]) !== null && _c !== void 0 ? _c : classes.positionCenter;
    const renderContent = () => {
        var _a, _b;
        const isLargeHeight = height === 'LARGE';
        const hasImageContent = ((_a = props.image) === null || _a === void 0 ? void 0 : _a.url) || (((_b = props.mobileImage) === null || _b === void 0 ? void 0 : _b.url) && !shoppableImage && !shoppableVideo);
        if (hasVideo && isLargeHeight) {
            return _jsx(VideoContainer, Object.assign({}, props));
        }
        if (hasImageContent) {
            return (_jsx(ImageContainer, Object.assign({}, props, { classNames: classes, squareImage: true, isShowMore: isShowMore, newStyle: newStyle, linksCount: links === null || links === void 0 ? void 0 : links.length })));
        }
        if (shoppableVideo && isLargeHeight) {
            return _jsx(ShoppableVideo, Object.assign({}, shoppableVideo, { mobileImage: props === null || props === void 0 ? void 0 : props.mobileImage }));
        }
        if (shoppableImage && isLargeHeight) {
            return _jsx(ShoppableImage, Object.assign({}, shoppableImage, { mobileImage: props.mobileImage }));
        }
    };
    const gridAlignmentClass = getTextAlignmentClass(textAlignment, classes);
    const getLinkGridSize = (index, totalLinks) => ({
        xs: 12,
        md: totalLinks === 3 && index === 2 ? 12 : 6,
    });
    const getLinkJustification = (index, totalLinks) => totalLinks === 3 && index === 2 ? 'center' : 'flex-start';
    const getLinkClassName = (classNames, isButton, index, totalLinks) => {
        const className = clsx(classNames.link, classes.link, !isButton ? classNames.linkText : '', totalLinks === 3 && index === 2 ? classes.thirdLink : '');
        return className;
    };
    const renderLinkContainer = (classNames) => {
        const isButton = (linkStyle === null || linkStyle === void 0 ? void 0 : linkStyle.toUpperCase()) === 'BUTTON';
        return (_jsx(Grid2, { container: true, spacing: 2, className: clsx(classes.linkGrid, gridAlignmentClass, 
            // Only apply mobile grid class on mobile for new style
            isMobile && shouldUseNewMobileLayout && classes.mobileLinkGrid), children: links === null || links === void 0 ? void 0 : links.map((link, index) => {
                var _a;
                if (!(link === null || link === void 0 ? void 0 : link.label))
                    return null;
                return (_jsx(Grid2, { size: getLinkGridSize(index, links.length), container: true, justifyContent: getLinkJustification(index, links.length), className: classes.linkItem, children: _jsx(Link, Object.assign({ button: isButton, variant: buttonVariant !== null && buttonVariant !== void 0 ? buttonVariant : 'tertiary', thin: true, className: getLinkClassName(classNames, isButton, index, links.length) }, link, { triggerLinkAnalytics: contentFeatureAnalytics })) }, (_a = link.id) !== null && _a !== void 0 ? _a : `link-${index}`));
            }) }));
    };
    return (_jsxs(Box, { ref: containerRef, className: clsx(classes === null || classes === void 0 ? void 0 : classes.container, bgClass, positionClass, getMinHeightClass(height, classes)), style: isHexColor ? { backgroundColor: hexBackgroundColor, color: hexTextColor } : {}, "data-testid": 'content-feature-container', sx: Object.assign({}, (shouldUseNewMobileLayout &&
            isShowMore &&
            isMobile &&
            initialHeight && {
            height: `${initialHeight}px`, // Use captured initial height
            maxHeight: `${initialHeight}px`, // Prevent expansion
            overflow: 'hidden', // Hide any overflow from the main container
        })), children: [renderContent(), shouldUseNewMobileLayout && isShowMore && (_jsx("div", { className: classes.mobileExpandedOverlay, style: isHexColor ? { backgroundColor: hexBackgroundColor } : {} })), shouldUseNewMobileLayout && _jsx(_Fragment, { children: renderLinkContainer(classes) }), _jsx("div", { className: clsx(shouldUseNewMobileLayout && isShowMore && classes.expandedContent), style: { width: isMobile ? '100%' : '50%' }, children: _jsx(ContentContainer, Object.assign({}, props, { heading: renderHeading, textContentPosition: textContentPosition, titleTextVariant: titleTextVariant, classNames: classes, headingColor: titleHeadingColor, headingTag: headingTag, contentFeatureLinkContainer: shouldUseNewMobileLayout ? undefined : renderLinkContainer, isFromContentFeature: true, isShowMore: shouldUseNewMobileLayout ? isShowMore : undefined, setIsShowMore: shouldUseNewMobileLayout ? setIsShowMore : undefined, renderNewStyle: newStyle, hasVideo: hasVideo })) })] }));
};
export default ContentFeature;
